/**
 * Ambient animals. They are scenery, not players: pure functions of the wall
 * clock, so everyone in a room sees roughly the same duck in the same place
 * without a single byte on the wire.
 */
import type { Critter } from '../../../shared/world.ts'
import { box, iso, poly } from './iso.ts'
import { mix, shade } from './color.ts'
import { drawBoat } from './props-extra.ts'

type Ctx = CanvasRenderingContext2D

const PAUSE = 1.6

export interface CritterPos { x: number; y: number; moving: boolean; dir: 1 | -1; k: number; /** Tile-space heading of the current leg. */ hx: number; hy: number }

export function critterAt(c: Critter, seconds: number, index: number): CritterPos {
  const n = c.loop.length
  const segs: number[] = []
  let total = 0
  for (let i = 0; i < n; i++) {
    const [ax, ay] = c.loop[i]!, [bx, by] = c.loop[(i + 1) % n]!
    const d = Math.hypot(bx - ax, by - ay) / c.speed + PAUSE
    segs.push(d)
    total += d
  }
  let t = (seconds + index * 7.3) % total
  for (let i = 0; i < n; i++) {
    const d = segs[i]!
    if (t < d) {
      const [ax, ay] = c.loop[i]!, [bx, by] = c.loop[(i + 1) % n]!
      const walk = d - PAUSE
      const k = Math.min(1, t / walk)
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2
      const sx = (bx - ax) - (by - ay)
      return { x: ax + (bx - ax) * e, y: ay + (by - ay) * e, moving: t < walk, dir: sx >= 0 ? 1 : -1, k: t, hx: bx - ax, hy: by - ay }
    }
    t -= d
  }
  const [x, y] = c.loop[0]!
  return { x, y, moving: false, dir: 1, k: 0, hx: 1, hy: 0 }
}

export function drawCritter(ctx: Ctx, c: Critter, p: CritterPos, t: number): void {
  if (c.kind === 'boat') { drawBoat(ctx, p.x, p.y, p.dir, 0, t); return }
  if (c.kind === 'car' || c.kind === 'tram') { vehicle(ctx, c, p, t); return }
  const base = iso(p.x, p.y, c.kind === 'koi' ? -0.27 : 0)
  ctx.save()
  ctx.translate(base.x, base.y)
  ctx.scale(p.dir, 1)
  switch (c.kind) {
    case 'duck': {
      const bob = Math.sin(t * 2.4 + p.x) * 1.2
      ctx.strokeStyle = 'rgba(255,255,255,0.45)'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.ellipse(0, 1, 11, 4, 0, 0, Math.PI * 2); ctx.stroke()
      ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.ellipse(0, -3 + bob, 8, 5, 0, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.moveTo(-8, -4 + bob); ctx.lineTo(-11, -8 + bob); ctx.lineTo(-6, -6 + bob); ctx.fill()
      ctx.beginPath(); ctx.arc(5, -9 + bob, 4, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#ff9f3c'; ctx.beginPath(); ctx.moveTo(8, -9 + bob); ctx.lineTo(12, -8 + bob); ctx.lineTo(8, -7 + bob); ctx.fill()
      ctx.fillStyle = '#2d2a3e'; ctx.beginPath(); ctx.arc(6, -10 + bob, 0.9, 0, Math.PI * 2); ctx.fill()
      break
    }
    case 'pigeon': {
      const peck = p.moving ? 0 : Math.max(0, Math.sin(t * 6 + p.y * 3)) * 3
      ctx.fillStyle = 'rgba(40,28,64,0.18)'; ctx.beginPath(); ctx.ellipse(0, 0, 7, 2.5, 0, 0, Math.PI * 2); ctx.fill()
      const hop = p.moving ? -Math.abs(Math.sin(t * 14)) * 2 : 0
      const body = c.color ?? '#9ea3b8'
      ctx.fillStyle = body; ctx.beginPath(); ctx.ellipse(0, -5 + hop, 7, 4.5, -0.1, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = shade(body, -0.12); ctx.beginPath(); ctx.moveTo(-6, -6 + hop); ctx.lineTo(-10, -3 + hop); ctx.lineTo(-5, -3 + hop); ctx.fill()
      ctx.fillStyle = shade(body, -0.2); ctx.beginPath(); ctx.arc(5, -9 + hop + peck, 3.4, 0, Math.PI * 2); ctx.fill()
      if (c.color) { ctx.fillStyle = '#e0443a'; ctx.beginPath(); ctx.arc(5, -13 + hop + peck, 1.8, 0, Math.PI * 2); ctx.fill() }
      else { ctx.fillStyle = '#7fd1b0'; ctx.fillRect(3, -7 + hop + peck, 3, 1.5) }
      ctx.fillStyle = '#e8a04a'; ctx.beginPath(); ctx.moveTo(8, -9 + hop + peck); ctx.lineTo(10.5, -8 + hop + peck); ctx.lineTo(8, -7.5 + hop + peck); ctx.fill()
      break
    }
    case 'cat': {
      ctx.fillStyle = 'rgba(40,28,64,0.2)'; ctx.beginPath(); ctx.ellipse(0, 0, 11, 3.5, 0, 0, Math.PI * 2); ctx.fill()
      const step = p.moving ? Math.sin(t * 12) * 1.2 : 0
      const tail = Math.sin(t * 2.2) * 0.5
      ctx.strokeStyle = '#4a4466'; ctx.lineWidth = 3; ctx.lineCap = 'round'
      ctx.beginPath(); ctx.moveTo(-8, -6); ctx.quadraticCurveTo(-15, -10 + tail * 6, -13, -18 + tail * 4); ctx.stroke()
      ctx.fillStyle = '#4a4466'
      ctx.beginPath(); ctx.ellipse(0, -6 + step * 0.3, 10, 6, 0, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.arc(8, -12, 5.5, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.moveTo(4.5, -15); ctx.lineTo(5, -21); ctx.lineTo(8.5, -16.5); ctx.fill()
      ctx.beginPath(); ctx.moveTo(8.5, -16.5); ctx.lineTo(12, -21); ctx.lineTo(12.5, -14.5); ctx.fill()
      ctx.fillStyle = '#ffe28a'; ctx.beginPath(); ctx.arc(7.5, -12.5, 1.2, 0, Math.PI * 2); ctx.arc(11, -12.5, 1.2, 0, Math.PI * 2); ctx.fill()
      break
    }
    case 'crab': {
      const sc = p.moving ? Math.sin(t * 20) : 0
      ctx.fillStyle = 'rgba(120,80,40,0.2)'; ctx.beginPath(); ctx.ellipse(0, 0, 8, 2.5, 0, 0, Math.PI * 2); ctx.fill()
      ctx.strokeStyle = '#d9483b'; ctx.lineWidth = 1.4
      for (let i = -1; i <= 1; i++) for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * 4, -3); ctx.lineTo(s * 8, -1 + i * 1.5 + sc * s); ctx.stroke() }
      ctx.fillStyle = '#ef5b4c'; ctx.beginPath(); ctx.ellipse(0, -4, 6, 4, 0, 0, Math.PI * 2); ctx.fill()
      for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * 8, -8 + Math.sin(t * 3) * 0.8, 2.6, 0, Math.PI * 2); ctx.fill() }
      ctx.fillStyle = '#2d2a3e'; ctx.fillRect(-2.5, -9, 1.3, 2.2); ctx.fillRect(1.2, -9, 1.3, 2.2)
      break
    }
    case 'koi': {
      const sw = Math.sin(t * 6 + p.x) * 0.4
      ctx.rotate(sw * 0.3)
      ctx.fillStyle = 'rgba(20,50,80,0.25)'; ctx.beginPath(); ctx.ellipse(1, 2, 8, 3, 0, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = (Math.floor(p.y * 10) % 2) ? '#ff8a3c' : '#ffffff'
      ctx.beginPath(); ctx.ellipse(0, 0, 7, 3, 0, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.moveTo(-6, 0); ctx.lineTo(-11, -3 + sw * 3); ctx.lineTo(-11, 3 + sw * 3); ctx.closePath(); ctx.fill()
      ctx.fillStyle = '#ff5d3c'; ctx.beginPath(); ctx.ellipse(2, -0.5, 2.6, 1.6, 0, 0, Math.PI * 2); ctx.fill()
      break
    }
    case 'butterfly': {
      const z = 26 + Math.sin(t * 1.7 + p.x) * 6
      ctx.fillStyle = 'rgba(40,28,64,0.12)'; ctx.beginPath(); ctx.ellipse(0, 0, 4, 1.5, 0, 0, Math.PI * 2); ctx.fill()
      const f = Math.abs(Math.sin(t * 14))
      ctx.translate(0, -z)
      ctx.fillStyle = '#ffb3d1'
      ctx.beginPath(); ctx.ellipse(-3 * f, -2, 4 * f + 0.5, 3, -0.4, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.ellipse(3 * f, -2, 4 * f + 0.5, 3, 0.4, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#ffd166'
      ctx.beginPath(); ctx.ellipse(-2.4 * f, 1.5, 2.6 * f + 0.4, 2, -0.3, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.ellipse(2.4 * f, 1.5, 2.6 * f + 0.4, 2, 0.3, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = '#3a3552'; ctx.fillRect(-0.6, -3, 1.2, 6)
      break
    }
  }
  ctx.restore()
}

/** Cars and the tram: boxes in world space, oriented along the leg they drive. */
function vehicle(ctx: Ctx, c: Critter, p: CritterPos, t: number): void {
  const alongX = Math.abs(p.hx) >= Math.abs(p.hy)
  const tram = c.kind === 'tram'
  const len = tram ? 3.2 : 1.5, wid = tram ? 0.95 : 0.72
  const col = c.color ?? (tram ? '#4fc3a1' : '#ff6b6b')
  const [x0, y0, w, d] = alongX ? [p.x - len / 2, p.y - wid / 2, len, wid] : [p.x - wid / 2, p.y - len / 2, wid, len]
  const bump = p.moving ? Math.abs(Math.sin(t * 14 + p.x)) * 0.02 : 0
  // Soft shadow.
  poly(ctx, [iso(x0 + 0.1, y0 + 0.1), iso(x0 + w + 0.1, y0 + 0.1), iso(x0 + w + 0.15, y0 + d + 0.15), iso(x0 + 0.1, y0 + d + 0.15)], 'rgba(30,25,50,0.22)')
  if (tram) {
    box(ctx, x0, y0, 0.15 + bump, w, d, 1.15, col)
    const glass = '#cfeaf5'
    // A band of windows on both visible faces.
    if (alongX) {
      for (let u = x0 + 0.2; u < x0 + w - 0.3; u += 0.45) poly(ctx, [iso(u, y0 + d + 0.002, 0.6), iso(u + 0.32, y0 + d + 0.002, 0.6), iso(u + 0.32, y0 + d + 0.002, 1.05), iso(u, y0 + d + 0.002, 1.05)], glass)
      poly(ctx, [iso(x0 + w + 0.002, y0 + 0.15, 0.55), iso(x0 + w + 0.002, y0 + d - 0.15, 0.55), iso(x0 + w + 0.002, y0 + d - 0.15, 1.05), iso(x0 + w + 0.002, y0 + 0.15, 1.05)], shade(glass, -0.1))
    } else {
      for (let u = y0 + 0.2; u < y0 + d - 0.3; u += 0.45) poly(ctx, [iso(x0 + w + 0.002, u, 0.6), iso(x0 + w + 0.002, u + 0.32, 0.6), iso(x0 + w + 0.002, u + 0.32, 1.05), iso(x0 + w + 0.002, u, 1.05)], shade(glass, -0.1))
      poly(ctx, [iso(x0 + 0.15, y0 + d + 0.002, 0.55), iso(x0 + w - 0.15, y0 + d + 0.002, 0.55), iso(x0 + w - 0.15, y0 + d + 0.002, 1.05), iso(x0 + 0.15, y0 + d + 0.002, 1.05)], glass)
    }
    box(ctx, x0 + 0.1, y0 + 0.1, 1.3 + bump, w - 0.2, d - 0.2, 0.1, '#ffffff')
    const pole = iso(p.x, p.y, 1.4)
    ctx.strokeStyle = '#3a3552'; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(pole.x - 6, pole.y); ctx.lineTo(pole.x, pole.y - 14); ctx.lineTo(pole.x + 6, pole.y - 18); ctx.stroke()
    return
  }
  box(ctx, x0, y0, 0.15 + bump, w, d, 0.32, col)
  const [cx0, cy0, cw, cd] = alongX ? [x0 + w * 0.28, y0 + 0.06, w * 0.46, d - 0.12] : [x0 + 0.06, y0 + d * 0.28, w - 0.12, d * 0.46]
  box(ctx, cx0, cy0, 0.47 + bump, cw, cd, 0.3, mix(col, '#ffffff', 0.15))
  const g = '#bfe6f5'
  if (alongX) poly(ctx, [iso(cx0 + 0.05, cy0 + cd + 0.002, 0.5), iso(cx0 + cw - 0.05, cy0 + cd + 0.002, 0.5), iso(cx0 + cw - 0.05, cy0 + cd + 0.002, 0.74), iso(cx0 + 0.05, cy0 + cd + 0.002, 0.74)], g)
  else poly(ctx, [iso(cx0 + cw + 0.002, cy0 + 0.05, 0.5), iso(cx0 + cw + 0.002, cy0 + cd - 0.05, 0.5), iso(cx0 + cw + 0.002, cy0 + cd - 0.05, 0.74), iso(cx0 + cw + 0.002, cy0 + 0.05, 0.74)], shade(g, -0.1))
  ctx.fillStyle = '#2d2a3e'
  const wheels: Array<[number, number]> = alongX ? [[x0 + 0.3, y0 + d], [x0 + w - 0.3, y0 + d]] : [[x0 + w, y0 + 0.3], [x0 + w, y0 + d - 0.3]]
  for (const [wx, wy] of wheels) { const q = iso(wx, wy, 0.14); ctx.beginPath(); ctx.ellipse(q.x, q.y, 4.5, 4.5, 0, 0, Math.PI * 2); ctx.fill() }
}
